/**
 * FlightAreaPanel — summary of the mission's take-off point and flight area under the map
 * (mission-map unification, PR6): area type, vertices, length/area (from the server's validation
 * endpoint), the corridor width field (LineString only — the same value the map popup edits), for a
 * circle its centre, area, perimeter and the radius field (during the circle's map edit it writes the
 * radius draft, see FlightAreaLayer), the take-off point status, server errors/warnings, and a
 * collapsible coordinate table.
 */
import { useState } from 'react';
import {
  AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, FileUp, Info, Loader2, Lock, Trash2, XCircle,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  classifyFlightArea, FLIGHT_AREA_KIND, getVertices, getCorridorWidthIssue, formatMeters, formatArea,
  localizedIssue, getCircleCenter, getCircleRadiusIssue, CIRCLE_DRAFT_IDLE,
} from '../../lib/flightArea';

const COLLAPSE_ABOVE_VERTICES = 10;

const Row = ({ label, children }) => (
  <div className="flex items-baseline justify-between gap-3 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="font-medium text-slate-800 text-right">{children}</span>
  </div>
);

const Note = ({ tone, icon: Icon, children, testId }) => {
  const tones = {
    ok: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    error: 'bg-red-50 border-red-200 text-red-800',
    info: 'bg-slate-50 border-slate-200 text-slate-700',
  };
  return (
    <div className={`flex items-start gap-2 rounded-md border px-2.5 py-1.5 text-xs ${tones[tone]}`} data-testid={testId}>
      <Icon className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
      <span>{children}</span>
    </div>
  );
};

export const legacyReasonText = (reason, fa) => ({
  holes: fa.reasonHoles,
  points: fa.reasonPoints,
  malformed: fa.reasonMalformed,
}[reason] || fa.reasonUnsupported);

export const FlightAreaPanel = ({
  mode = 'readonly',
  locked = false,
  geometry,
  corridorWidthM,
  onCorridorWidthChange,
  circleRadiusM,
  onCircleRadiusChange,
  // The radius draft of a circle's map edit (CIRCLE_DRAFT_IDLE outside it) and its writer.
  circleRadiusDraft = CIRCLE_DRAFT_IDLE,
  onCircleRadiusDraftInput,
  circleRadiusLimits = null,
  latitude,
  longitude,
  validation,
  settings,
  onDeleteArea,
  onImportClick,
  importAvailable = false,
  highlightWidth = false,
  // A draw / edit / delete session is open on the map — import and delete would race the toolbar.
  sessionActive = false,
}) => {
  const { t, language, translate } = useLanguage();
  const fa = t.flightArea || {};
  const locale = t.locale || (language === 'cs' ? 'cs-CZ' : 'en-GB');
  const editable = mode === 'edit' && !locked;

  const classified = classifyFlightArea(geometry);
  const kind = classified.kind;
  const isLine = kind === FLIGHT_AREA_KIND.LINE;
  const isCircle = kind === FLIGHT_AREA_KIND.CIRCLE;
  const vertices = getVertices(geometry);
  const center = getCircleCenter(geometry);
  // The coordinate table: a circle's centre instead of vertices.
  const tablePoints = isCircle ? [center] : vertices;
  const hasTakeoff = Number.isFinite(latitude) && Number.isFinite(longitude);
  const [showTable, setShowTable] = useState(vertices.length <= COLLAPSE_ABOVE_VERTICES);

  const result = validation?.result;
  const summary = result?.summary;
  const takeoff = result?.takeoff;
  const widthIssue = isLine ? getCorridorWidthIssue(corridorWidthM, settings) : null;
  const range = settings ? { min: settings.corridor_width_min_m, max: settings.corridor_width_max_m } : null;
  // During the circle's map edit the field shows and writes the draft (an empty field is 'required'),
  // otherwise the form; during another map session (drawing, deleting) it waits for its end.
  const radiusInSession = !!circleRadiusDraft?.active;
  const radiusValue = radiusInSession ? circleRadiusDraft.value : circleRadiusM;
  const radiusIssue = isCircle ? getCircleRadiusIssue(radiusValue, circleRadiusLimits) : null;
  const radiusDisabled = !editable || (sessionActive && !radiusInSession);

  const takeoffError = (result?.errors || []).find((e) => e.code === 'takeoff_point_too_far');

  const takeoffNote = () => {
    if (!hasTakeoff) {
      // Read-only: the legacy callout below says the same, with a hint where to fix it.
      return mode === 'edit' ? <Note tone="error" icon={XCircle} testId="takeoff-status">{fa.takeoffRequired}</Note> : null;
    }
    if (!takeoff?.status) return null;
    const hasDistance = Number.isFinite(takeoff.distance_m);
    if (takeoff.status === 'too_far') {
      // Beyond the locally measurable range the server reports no distance — show its own sentence.
      let text;
      if (hasDistance) {
        text = translate('flightArea.takeoffTooFar', {
          distance: formatMeters(takeoff.distance_m, locale), max: formatMeters(settings?.takeoff_max_distance_m, locale),
        });
      } else if (takeoffError) {
        text = localizedIssue(takeoffError, language);
      } else {
        text = fa.takeoffTooFarUnmeasured;
      }
      return <Note tone="error" icon={XCircle} testId="takeoff-status">{text}</Note>;
    }
    if (!hasDistance) return null;
    const distance = formatMeters(takeoff.distance_m, locale);
    if (takeoff.status === 'warning') {
      return (
        <Note tone="warning" icon={AlertTriangle} testId="takeoff-status">
          {translate('flightArea.takeoffWarning', { distance, limit: formatMeters(settings?.takeoff_warning_distance_m, locale) })}
        </Note>
      );
    }
    return (
      <Note tone="ok" icon={CheckCircle2} testId="takeoff-status">
        {takeoff.distance_m === 0 ? fa.takeoffInside : translate('flightArea.takeoffNear', { distance })}
      </Note>
    );
  };
  const takeoffNode = takeoffNote();

  // A corridor-width or take-off problem the panel shows on its own line is not repeated from the
  // server's list — but only when that line is really rendered, otherwise the server text is the only
  // place the problem shows up.
  const widthLineShown = isLine && (mode === 'edit'
    ? widthIssue === 'required' || (widthIssue === 'range' && !!range)
    : widthIssue === 'required');
  const radiusLineShown = isCircle && (mode === 'edit'
    ? radiusIssue === 'required' || (radiusIssue === 'range' && !!circleRadiusLimits)
    : radiusIssue === 'required');
  const takeoffCoveredCode = takeoffNode && hasTakeoff
    ? { too_far: 'takeoff_point_too_far', warning: 'takeoff_point_far_from_area' }[takeoff?.status] || null
    : null;
  const shownErrors = (result?.errors || []).filter((e) =>
    !(widthLineShown && e.code?.startsWith('corridor_width'))
    && !(radiusLineShown && e.code?.startsWith('circle_radius'))
    && e.code !== takeoffCoveredCode);
  const shownWarnings = (result?.warnings || []).filter((w) => w.code !== takeoffCoveredCode);

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-3" data-testid="flight-area-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-800">{fa.area}</span>
          {kind === FLIGHT_AREA_KIND.POLYGON && <Badge variant="outline" className="text-xs">{fa.typePolygon}</Badge>}
          {isLine && <Badge variant="outline" className="text-xs">{fa.typeLine}</Badge>}
          {isCircle && <Badge variant="outline" className="text-xs">{fa.typeCircle}</Badge>}
          {validation?.loading && geometry && (
            <span className="flex items-center gap-1 text-xs text-slate-500">
              <Loader2 className="h-3 w-3 animate-spin" /> {fa.validating}
            </span>
          )}
        </div>
        {mode === 'edit' && (
          <div className="flex items-center gap-2">
            {onImportClick && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 gap-1.5 text-xs"
                disabled={!editable || !importAvailable || sessionActive}
                title={!importAvailable ? fa.importAfterSave : (sessionActive ? fa.finishMapSession : undefined)}
                onClick={onImportClick}
                data-testid="import-geometry-btn"
              >
                <FileUp className="h-3.5 w-3.5" /> {fa.importFromFile}
              </Button>
            )}
            {geometry && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 gap-1.5 text-xs text-red-700 border-red-200 hover:bg-red-50"
                disabled={!editable || sessionActive}
                title={sessionActive ? fa.finishMapSession : undefined}
                onClick={onDeleteArea}
                data-testid="delete-flight-area-btn"
              >
                <Trash2 className="h-3.5 w-3.5" /> {fa.deleteArea}
              </Button>
            )}
          </div>
        )}
      </div>

      {mode === 'edit' && onImportClick && !importAvailable && (
        <p className="text-xs text-slate-500 flex items-center gap-1"><Info className="h-3 w-3" /> {fa.importAfterSave}</p>
      )}
      {locked && <Note tone="info" icon={Lock}>{fa.lockedCompleted}</Note>}
      {editable && sessionActive && (
        <Note tone="warning" icon={AlertTriangle} testId="map-session-note">{fa.finishMapSession}</Note>
      )}

      {kind === FLIGHT_AREA_KIND.NONE && (
        <Note tone={mode === 'edit' ? 'error' : 'warning'} icon={mode === 'edit' ? XCircle : AlertTriangle} testId="area-status">
          {mode === 'edit' ? `${fa.areaRequired}. ${fa.drawHint}` : fa.legacyNoArea}
        </Note>
      )}
      {kind === FLIGHT_AREA_KIND.INVALID && (
        <Note tone="error" icon={XCircle} testId="area-status">
          {translate('flightArea.legacyInvalidArea', {
            // The server sentence ends with a full stop; the template adds its own after the bracket.
            reason: shownErrors[0]
              ? localizedIssue(shownErrors[0], language).replace(/[.\s]+$/, '')
              : legacyReasonText(classified.reason, fa),
          })}
        </Note>
      )}

      {(kind === FLIGHT_AREA_KIND.POLYGON || isLine) && (
        <div className="grid gap-1.5 sm:grid-cols-[repeat(auto-fill,minmax(12rem,16rem))] sm:gap-x-8">
          <Row label={fa.vertexCount}>{summary?.vertex_count ?? vertices.length}</Row>
          {isLine ? (
            <Row label={fa.length}>{formatMeters(summary?.length_m, locale)}</Row>
          ) : (
            <Row label={fa.areaSize}>{formatArea(summary?.area_m2, locale)}</Row>
          )}
          {isLine && summary?.corridor_area_m2 !== null && summary?.corridor_area_m2 !== undefined && (
            <Row label={fa.corridorArea}>{formatArea(summary.corridor_area_m2, locale)}</Row>
          )}
          {!isLine && <Row label={fa.perimeter}>{formatMeters(summary?.length_m, locale)}</Row>}
          {isLine && mode === 'readonly' && (
            <Row label={fa.corridorWidth}>{Number.isFinite(Number(corridorWidthM)) && corridorWidthM !== null ? `${corridorWidthM} m` : '—'}</Row>
          )}
        </div>
      )}

      {isLine && mode === 'edit' && (
        <div className={`space-y-1 rounded-md p-2 ${highlightWidth || widthIssue ? 'bg-amber-50 ring-1 ring-amber-300' : 'bg-slate-50'}`}>
          <label htmlFor="corridor-width-input" className="text-xs font-medium text-slate-700">{fa.corridorWidth} *</label>
          <Input
            id="corridor-width-input"
            type="number"
            inputMode="decimal"
            min={range?.min}
            max={range?.max}
            step="any"
            value={corridorWidthM ?? ''}
            disabled={!editable}
            onChange={(e) => onCorridorWidthChange?.(e.target.value === '' ? null : e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
            className="h-8 w-40 bg-white"
            data-testid="corridor-width-input"
          />
          {range && <p className="text-xs text-slate-500">{translate('flightArea.corridorWidthHelper', range)}</p>}
          {widthIssue === 'required' && <p className="text-xs text-red-700">{fa.corridorWidthRequired}</p>}
          {widthIssue === 'range' && range && <p className="text-xs text-red-700">{translate('flightArea.corridorWidthRange', range)}</p>}
        </div>
      )}
      {isLine && mode === 'readonly' && widthIssue === 'required' && (
        <Note tone="warning" icon={AlertTriangle}>{fa.legacyNoWidth}</Note>
      )}

      {isCircle && (
        <div className="grid gap-1.5 sm:grid-cols-[repeat(auto-fill,minmax(12rem,16rem))] sm:gap-x-8" data-testid="circle-summary">
          <Row label={fa.circleCenter}>
            <span className="font-mono text-xs">{center.lat.toFixed(6)}, {center.lng.toFixed(6)}</span>
          </Row>
          <Row label={fa.areaSize}>{formatArea(summary?.area_m2, locale)}</Row>
          <Row label={fa.perimeter}>{formatMeters(summary?.length_m, locale)}</Row>
          {mode === 'readonly' && (
            <Row label={fa.circleRadius}>{radiusIssue === 'required' ? '—' : `${circleRadiusM} m`}</Row>
          )}
        </div>
      )}

      {isCircle && mode === 'edit' && (
        <div className={`space-y-1 rounded-md p-2 ${radiusIssue ? 'bg-amber-50 ring-1 ring-amber-300' : 'bg-slate-50'}`}>
          <label htmlFor="circle-radius-input" className="text-xs font-medium text-slate-700">{fa.circleRadius} *</label>
          <Input
            id="circle-radius-input"
            type="number"
            inputMode="decimal"
            min={circleRadiusLimits?.min}
            max={circleRadiusLimits?.max}
            step="any"
            value={radiusValue ?? ''}
            disabled={radiusDisabled}
            title={radiusDisabled && sessionActive ? fa.finishMapSession : undefined}
            onChange={(e) => {
              const value = e.target.value === '' ? null : e.target.value;
              if (radiusInSession) onCircleRadiusDraftInput?.(value);
              else onCircleRadiusChange?.(value);
            }}
            onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
            className="h-8 w-40 bg-white"
            data-testid="circle-radius-input"
          />
          {circleRadiusLimits && <p className="text-xs text-slate-500">{translate('flightArea.circleRadiusHelper', circleRadiusLimits)}</p>}
          {radiusIssue === 'required' && <p className="text-xs text-red-700">{fa.circleRadiusRequired}</p>}
          {radiusIssue === 'range' && circleRadiusLimits && (
            <p className="text-xs text-red-700">{translate('flightArea.circleRadiusRange', circleRadiusLimits)}</p>
          )}
        </div>
      )}
      {isCircle && mode === 'readonly' && radiusIssue === 'required' && (
        <Note tone="warning" icon={AlertTriangle}>{fa.legacyNoRadius}</Note>
      )}

      {takeoffNode}
      {mode === 'readonly' && !hasTakeoff && <Note tone="warning" icon={AlertTriangle}>{fa.legacyNoTakeoff}</Note>}

      {kind !== FLIGHT_AREA_KIND.INVALID && shownErrors.map((e) => (
        <Note key={e.code} tone="error" icon={XCircle}>{localizedIssue(e, language)}</Note>
      ))}
      {shownWarnings.map((w) => (
        <Note key={w.code} tone="warning" icon={AlertTriangle}>{localizedIssue(w, language)}</Note>
      ))}
      {validation?.failed && <Note tone="info" icon={Info}>{fa.validationUnavailable}</Note>}

      {(tablePoints.length > 0 || hasTakeoff) && (
        <div className="border-t border-slate-100 pt-2">
          <button
            type="button"
            onClick={() => setShowTable((v) => !v)}
            className="flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900"
            data-testid="toggle-coordinates-btn"
          >
            {showTable ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            {showTable ? fa.hideCoordinates : fa.showCoordinates} ({tablePoints.length})
          </button>
          {showTable && (
            <div className="mt-2 max-h-[220px] overflow-y-auto rounded border border-slate-100">
              <table className="w-full text-xs" data-testid="flight-area-coordinates">
                <thead className="bg-slate-50 text-left text-slate-500 sticky top-0">
                  <tr>
                    <th className="px-2 py-1 w-28">#</th>
                    <th className="px-2 py-1">{fa.latitude}</th>
                    <th className="px-2 py-1">{fa.longitude}</th>
                  </tr>
                </thead>
                <tbody>
                  {hasTakeoff && (
                    <tr className="border-t border-slate-100 bg-blue-50/50">
                      <td className="px-2 py-1 font-medium text-slate-700">{fa.takeoffPoint}</td>
                      <td className="px-2 py-1 font-mono">{latitude.toFixed(6)}</td>
                      <td className="px-2 py-1 font-mono">{longitude.toFixed(6)}</td>
                    </tr>
                  )}
                  {tablePoints.map((v, idx) => (
                    <tr key={idx} className="border-t border-slate-100">
                      <td className="px-2 py-1 text-slate-500">{isCircle ? fa.circleCenter : idx + 1}</td>
                      <td className="px-2 py-1 font-mono">{v.lat.toFixed(6)}</td>
                      <td className="px-2 py-1 font-mono">{v.lng.toFixed(6)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FlightAreaPanel;
